import { useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Alert,
    Box,
    Button,
    Chip,
    Collapse,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/client';
import { useSnackbar } from '@/shared/snackbar/SnackbarContext';
import { useActivateMercedes, useDeactivateMercedes, useMercedesStatus, useUpdateVehicle, VEHICLES_KEY } from '../api';
import type { ServiceStatus, VehicleDetail } from '../types';
import { ConfirmDialog } from './ConfirmDialog';
import { MercedesChip } from './VehicleTable';

const SERVICE_COLOR: Record<ServiceStatus, 'success' | 'warning' | 'error' | 'default'> = {
    ACTIVE: 'success',
    ACTIVATION_PENDING: 'warning',
    ACTIVATION_FAILED: 'error',
    NOT_AVAILABLE: 'error',
    INACTIVE: 'default',
};

type ServiceLabelKey =
    'vehicles.services.position15' | 'vehicles.services.position30' | 'vehicles.services.position120';

/** The three position services the BE books (mercedes-constants.js); unknown ids fall back to the BE name. */
const SERVICE_LABEL: Record<string, ServiceLabelKey> = {
    '1018': 'vehicles.services.position15',
    '1019': 'vehicles.services.position30',
    '1005': 'vehicles.services.position120',
};

function readVinForFin(details: unknown): string | null {
    const vin = typeof details === 'object' && details !== null && 'vinForFin' in details ? details.vinForFin : null;
    return typeof vin === 'string' ? vin : null;
}

/** Connection state, the three position services with live status, activate / disconnect, FIN hint. */
export function MercedesSection({ vehicle }: { vehicle: VehicleDetail }) {
    const { t } = useTranslation();
    const { showError, showSuccess } = useSnackbar();
    const connected = vehicle.mercedesStatus !== 'NONE';
    const inProgress = vehicle.mercedesStatus === 'CONNECTING' || vehicle.mercedesStatus === 'DISCONNECTING';

    const status = useMercedesStatus(vehicle.id, connected);
    const queryClient = useQueryClient();
    // Refresh = detail (connection state, delete lock) and service status together.
    const refresh = () => void queryClient.invalidateQueries({ queryKey: [...VEHICLES_KEY, vehicle.id] });
    const activate = useActivateMercedes();
    const deactivate = useDeactivateMercedes();
    const update = useUpdateVehicle();
    const [vinForFin, setVinForFin] = useState<string | null>(null);
    const [confirmDisconnect, setConfirmDisconnect] = useState(false);
    // One connection action at a time — applying a corrected VIN counts as one.
    const busy = inProgress || activate.isPending || deactivate.isPending || update.isPending;

    function onActivate() {
        setVinForFin(null);
        activate.mutate(vehicle.id, {
            onSuccess: () => showSuccess(t('vehicles.mercedes.activated')),
            onError: (error) => {
                if (error instanceof ApiError && error.errorName === 'FinInsteadOfVin') {
                    setVinForFin(readVinForFin(error.details));
                } else {
                    showError(error);
                }
            },
        });
    }

    function onDisconnect() {
        deactivate.mutate(vehicle.id, {
            onSuccess: () => showSuccess(t('vehicles.mercedes.disconnected')),
            onError: showError,
            onSettled: () => setConfirmDisconnect(false),
        });
    }

    const pending = status.data?.some((service) => service.status === 'ACTIVATION_PENDING') ?? false;

    return (
        <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MercedesChip status={vehicle.mercedesStatus} />
                {connected && (
                    <IconButton
                        size="small"
                        aria-label={t('common.refresh')}
                        disabled={status.isFetching}
                        onClick={refresh}
                    >
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
            {inProgress && (
                <Typography variant="caption" color="text.secondary">
                    {t('vehicles.mercedes.inProgress')}
                </Typography>
            )}
            {/* Late-arriving status slides in instead of pushing the buttons down in one jump. */}
            {connected && status.isError && (
                <Collapse in appear>
                    <Alert severity="warning">{t('vehicles.mercedes.statusUnavailable')}</Alert>
                </Collapse>
            )}
            {/* Cached status survives a failed refresh; show only a non-error result. */}
            {connected && status.data && !status.isError && (
                <Collapse in appear>
                    <Table size="small" sx={{ '& td:first-of-type': { pl: 0 }, '& td:last-of-type': { pr: 0 } }}>
                        <TableBody>
                            {status.data.map((service) => (
                                <TableRow key={service.serviceId}>
                                    <TableCell>
                                        {SERVICE_LABEL[service.serviceId]
                                            ? t(SERVICE_LABEL[service.serviceId] as ServiceLabelKey)
                                            : service.serviceName}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Chip
                                            variant="outlined"
                                            color={SERVICE_COLOR[service.status]}
                                            label={t(`vehicles.serviceStatus.${service.status}`)}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {pending && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            {t('vehicles.mercedes.pendingHint')}
                        </Alert>
                    )}
                </Collapse>
            )}
            <Typography variant="body2" color="text.secondary">
                {t('vehicles.mercedes.explain')}
            </Typography>
            {vinForFin && (
                <Collapse in appear>
                    <Alert
                        severity="warning"
                        action={
                            <Button
                                size="small"
                                color="inherit"
                                disabled={update.isPending}
                                onClick={() =>
                                    update.mutate(
                                        { id: vehicle.id, vin: vinForFin },
                                        { onSuccess: () => setVinForFin(null), onError: showError },
                                    )
                                }
                            >
                                {t('vehicles.mercedes.applyVin')}
                            </Button>
                        }
                    >
                        {t('vehicles.mercedes.finHint')} <strong>{vinForFin}</strong>
                    </Alert>
                </Collapse>
            )}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {connected ? (
                    <>
                        <Button variant="outlined" disabled={busy} onClick={onActivate}>
                            {t('vehicles.mercedes.reactivate')}
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            disabled={busy}
                            onClick={() => setConfirmDisconnect(true)}
                        >
                            {t('vehicles.mercedes.disconnect')}
                        </Button>
                    </>
                ) : (
                    <Button variant="contained" disabled={busy} onClick={onActivate}>
                        {t('vehicles.mercedes.activate')}
                    </Button>
                )}
            </Box>
            <ConfirmDialog
                open={confirmDisconnect}
                title={t('vehicles.mercedes.disconnectTitle')}
                message={t('vehicles.mercedes.disconnectText')}
                confirmLabel={t('vehicles.mercedes.disconnect')}
                color="error"
                busy={deactivate.isPending}
                onCancel={() => setConfirmDisconnect(false)}
                onConfirm={onDisconnect}
            />
        </Stack>
    );
}
